import type { Viewer } from '@xeokit/xeokit-sdk';
import { rvtCategoryName } from '../explorer/rvt-tree';
import { adaptStepMetadata } from '../explorer/step-tree';
import { detectMetadataFormat } from '../metadata-format';
import { detectPropertyType, type PropertyDataType } from './evaluate';

export const NAME_PROPERTY = 'Name';
export const TYPE_PROPERTY = 'Type';

const MAX_VALUES_PER_PROPERTY = 100;

export interface CollectedProperties {
  propertyNames: string[];
  valuesByProperty: Map<string, string[]>;
  propertyTypeMap: Map<string, PropertyDataType>;
  objectProps: Map<string, Map<string, string[]>>;
  objectType: Map<string, string>;
  storeyByEntityId: Map<string, string>;
  ifcTypes: string[];
  propertyNamesByType: Map<string, string[]>;
  totalObjects: number;
}

interface MetaIndexEntry {
  name: string;
  type: string;
  storey: string;
  props: { name: string; value: string }[];
}

const STOREY_TYPES = new Set(['IfcBuildingStorey', 'IfcFacilityPart']);
const RVT_STOREY_PROPERTIES = new Set(['level', 'base level', 'reference level', 'schedule level']);
// Revit's own label for "no level"; it must not become a filterable storey.
const RVT_NO_LEVEL = '(none)';

interface RawProperty {
  name?: string;
  value?: unknown;
}
interface RawMetaObject {
  id: string | number;
  name?: string;
  type?: string;
  parent?: string | number | null;
  children?: number[];
  propertySetIds?: (string | number)[];
}
interface RawPropertySet {
  id: string | number;
  properties?: (number | RawProperty)[];
}
interface RawRvtElement {
  Id: string | number;
  Name?: string;
  class?: string;
  Type?: number | null;
  Family?: number | null;
  Category?: number | null;
  Level?: number | null;
  ParameterGroups?: number[];
  appearances?: string[];
}
interface RawParameterGroup {
  Parameters?: number[];
}
interface RawParameter {
  Name?: string;
  Value?: unknown;
  ValueString?: string | null;
}
interface RawMetadata {
  metaObjects?: RawMetaObject[];
  properties?: RawProperty[];
  propertySets?: RawPropertySet[];
  Elements?: RawRvtElement[];
  Parameters?: RawParameter[];
  ParameterGroups?: RawParameterGroup[];
  Categories?: { Name?: string }[];
}

function stringifyValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function buildIfcStoreyResolver(metaObjects: RawMetaObject[]): (id: string) => string {
  const byId = new Map<string, RawMetaObject>();
  const parentById = new Map<string, string>();
  for (const meta of metaObjects) byId.set(String(meta.id), meta);
  for (const meta of metaObjects) {
    if (Array.isArray(meta.children) && meta.children.length) {
      for (const childIndex of meta.children) {
        const child = metaObjects[childIndex];
        if (child) parentById.set(String(child.id), String(meta.id));
      }
    } else if (meta.parent != null) {
      parentById.set(String(meta.id), String(meta.parent));
    }
  }

  return (startId: string): string => {
    let currentId: string | undefined = startId;
    const seen = new Set<string>();
    while (currentId && !seen.has(currentId)) {
      seen.add(currentId);
      const meta = byId.get(currentId);
      if (!meta) break;
      if (meta.type && STOREY_TYPES.has(meta.type)) return meta.name || String(meta.id);
      currentId = parentById.get(currentId);
    }
    return '';
  };
}

function buildIfcIndex(metadata: RawMetadata): Map<string, MetaIndexEntry> {
  const index = new Map<string, MetaIndexEntry>();
  const allProperties = metadata.properties ?? [];
  const metaObjects = metadata.metaObjects ?? [];
  const propertySetsById = new Map<string, RawPropertySet>(
    (metadata.propertySets ?? []).map((propertySet) => [String(propertySet.id), propertySet]),
  );
  const resolveStorey = buildIfcStoreyResolver(metaObjects);

  for (const meta of metaObjects) {
    const props: { name: string; value: string }[] = [];
    for (const propertySetId of meta.propertySetIds ?? []) {
      const propertySet = propertySetsById.get(String(propertySetId));
      if (!propertySet) continue;
      for (const entry of propertySet.properties ?? []) {
        const property = typeof entry === 'number' ? allProperties[entry] : entry;
        if (property?.name)
          props.push({ name: property.name, value: stringifyValue(property.value) });
      }
    }
    index.set(String(meta.id), {
      name: meta.name ?? '',
      type: meta.type ?? '',
      storey: resolveStorey(String(meta.id)),
      props,
    });
  }

  return index;
}

function buildRvtIndex(metadata: RawMetadata): Map<string, MetaIndexEntry> {
  const index = new Map<string, MetaIndexEntry>();
  const parameterGroups = metadata.ParameterGroups ?? [];
  const parameters = metadata.Parameters ?? [];
  const elements = metadata.Elements ?? [];
  const categories = metadata.Categories ?? [];

  for (const element of elements) {
    const props: { name: string; value: string }[] = [];
    // `Level` indexes a Level element; elements without one still name it in a parameter.
    let storey = typeof element.Level === 'number' ? (elements[element.Level]?.Name ?? '') : '';
    for (const groupIndex of element.ParameterGroups ?? []) {
      const group = parameterGroups[groupIndex];
      if (!group) continue;
      for (const parameterIndex of group.Parameters ?? []) {
        const parameter = parameters[parameterIndex];
        if (!parameter?.Name) continue;
        const value = stringifyValue(parameter.ValueString ?? parameter.Value);
        props.push({ name: parameter.Name, value });
        if (
          !storey &&
          value &&
          value !== RVT_NO_LEVEL &&
          RVT_STOREY_PROPERTIES.has(parameter.Name.toLowerCase())
        ) {
          storey = value;
        }
      }
    }
    const entry = {
      name: element.Name ?? '',
      // The Revit category, so filters and grouping speak the same language as the
      // Explorer's classes tab. `class` is the Revit API class, a much coarser bucket.
      type: rvtCategoryName(elements, categories, element) || element.class || '',
      storey,
      props,
    };
    // Entities carry an appearance (`<Id>_<n>`) as originalSystemId, so key on both.
    index.set(String(element.Id), entry);
    for (const appearance of element.appearances ?? []) index.set(appearance, entry);
  }

  return index;
}

function buildModelIndex(metadata: RawMetadata | undefined): Map<string, MetaIndexEntry> {
  if (!metadata) return new Map();
  const format = detectMetadataFormat(metadata);
  if (format === 'rvt') return buildRvtIndex(metadata);
  if (format === 'step') return buildIfcIndex(adaptStepMetadata(metadata));
  // `ifc` and `scene` share the metaObjects/propertySets shape.
  if (Array.isArray(metadata.metaObjects)) return buildIfcIndex(metadata);
  return new Map();
}

function readFromMetaScene(viewer: Viewer, sourceId: string): MetaIndexEntry | undefined {
  const metaObject = (viewer.metaScene?.metaObjects as Record<string, any> | undefined)?.[sourceId];
  if (!metaObject) return undefined;
  const props: { name: string; value: string }[] = [];
  for (const propertySet of metaObject.propertySets ?? []) {
    for (const property of propertySet.properties ?? []) {
      if (property?.name)
        props.push({ name: property.name, value: stringifyValue(property.value) });
    }
  }

  let storey = '';
  const seen = new Set<unknown>();
  let ancestor = metaObject.parent;
  while (ancestor && !seen.has(ancestor)) {
    seen.add(ancestor);
    if (ancestor.type && STOREY_TYPES.has(ancestor.type)) {
      storey = ancestor.name || String(ancestor.id ?? '');
      break;
    }
    ancestor = ancestor.parent;
  }

  return { name: metaObject.name ?? '', type: metaObject.type ?? '', storey, props };
}

/** Map of Scene Entity id -> IFC type or Revit category, without collecting properties. */
export function collectObjectTypes(viewer: Viewer): Map<string, string> {
  const types = new Map<string, string>();

  const scene = viewer.scene as unknown as {
    models: Record<string, { metadata?: RawMetadata; objects?: Record<string, any> }>;
  };

  for (const modelId in scene.models) {
    const model = scene.models[modelId];
    const index = buildModelIndex(model?.metadata);
    const objects = model?.objects ?? {};

    for (const entityId in objects) {
      const entity = objects[entityId];
      const sourceId = entity?.originalSystemId != null ? String(entity.originalSystemId) : '';
      const type = (index.get(sourceId) ?? readFromMetaScene(viewer, sourceId))?.type;
      if (type) types.set(entityId, type);
    }
  }

  return types;
}

function addValue(target: Map<string, string[]>, name: string, value: string) {
  const existing = target.get(name);
  if (existing) existing.push(value);
  else target.set(name, [value]);
}

export function collectProperties(viewer: Viewer): CollectedProperties {
  const objectProps = new Map<string, Map<string, string[]>>();
  const objectType = new Map<string, string>();
  const storeyByEntityId = new Map<string, string>();
  const propertyNames = new Set<string>();
  const valuesByProperty = new Map<string, Set<string>>();
  const propertyNamesByTypeSet = new Map<string, Set<string>>();

  const scene = viewer.scene as unknown as {
    models: Record<string, { metadata?: RawMetadata; objects?: Record<string, any> }>;
  };

  for (const modelId in scene.models) {
    const model = scene.models[modelId];
    const index = buildModelIndex(model?.metadata);
    const objects = model?.objects ?? {};

    for (const entityId in objects) {
      const entity = objects[entityId];
      const sourceId = entity?.originalSystemId != null ? String(entity.originalSystemId) : '';
      const entry = index.get(sourceId) ?? readFromMetaScene(viewer, sourceId);

      const flat = new Map<string, string[]>();
      const name = entry?.name ?? '';
      const type = entry?.type ?? '';
      addValue(flat, NAME_PROPERTY, name);
      addValue(flat, TYPE_PROPERTY, type);
      if (entry) for (const property of entry.props) addValue(flat, property.name, property.value);

      objectProps.set(entityId, flat);
      if (type) objectType.set(entityId, type);
      if (entry?.storey) storeyByEntityId.set(entityId, entry.storey);

      const typeSet = type ? (propertyNamesByTypeSet.get(type) ?? new Set<string>()) : undefined;
      if (type && typeSet) propertyNamesByTypeSet.set(type, typeSet);

      for (const [propertyName, values] of flat) {
        propertyNames.add(propertyName);
        if (typeSet) typeSet.add(propertyName);
        let valueSet = valuesByProperty.get(propertyName);
        if (!valueSet) {
          valueSet = new Set<string>();
          valuesByProperty.set(propertyName, valueSet);
        }
        for (const value of values) {
          if (value && valueSet.size < MAX_VALUES_PER_PROPERTY) valueSet.add(value);
        }
      }
    }
  }

  const valuesByPropertyArray = new Map<string, string[]>();
  const propertyTypeMap = new Map<string, PropertyDataType>();
  for (const [propertyName, valueSet] of valuesByProperty) {
    const values = [...valueSet].sort();
    valuesByPropertyArray.set(propertyName, values);
    propertyTypeMap.set(propertyName, detectPropertyType(values));
  }

  const propertyNamesByType = new Map<string, string[]>();
  for (const [type, set] of propertyNamesByTypeSet) {
    propertyNamesByType.set(type, [...set].sort());
  }

  return {
    propertyNames: [...propertyNames].sort(),
    valuesByProperty: valuesByPropertyArray,
    propertyTypeMap,
    objectProps,
    objectType,
    storeyByEntityId,
    ifcTypes: [...propertyNamesByType.keys()].sort(),
    propertyNamesByType,
    totalObjects: objectProps.size,
  };
}
